/**
 * QuickReferenceCard Component
 * Displays "at a glance" jurisdiction info relevant to the current job
 *
 * Shows:
 * - Fee estimate for permit type
 * - Processing timeline (days)
 * - Department contact for trade
 * - NOC alert if project value > threshold
 * - Inspection scheduling info
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DollarSign,
  Clock,
  Phone,
  Mail,
  Calendar,
  AlertTriangle,
  FileText,
  ExternalLink,
  Info,
} from "lucide-react";
import type { Jurisdiction } from "@/hooks/useCompliance";

interface QuickReferenceCardProps {
  jurisdiction: Jurisdiction;
  trade: string;
  projectValue?: number;
}

/**
 * Get the relevant fee from the fee schedule based on trade/permit type
 */
function getRelevantFee(
  feeSchedule: Jurisdiction["feeSchedule"],
  trade: string
): { description: string; value: string } | null {
  if (!feeSchedule) return null;

  const tradeKey = trade.toLowerCase().replace(/[^a-z]/g, "_");

  // Check specialty work fees first
  if (feeSchedule.specialty_work) {
    // Map common trade names to fee schedule keys
    const tradeMapping: Record<string, string> = {
      roofing: "roofing",
      electrical: "electrical",
      plumbing: "plumbing",
      hvac: "hvac",
      mechanical: "hvac",
      pool: "in_ground_pool",
      windows: "window_door_replacement",
      doors: "window_door_replacement",
    };

    const feeKey = tradeMapping[tradeKey] || tradeKey;
    const specialtyFee = feeSchedule.specialty_work[feeKey];

    if (specialtyFee) {
      if (specialtyFee.rate) {
        return {
          description: `${trade} permit`,
          value: `$${specialtyFee.rate.toLocaleString()}`,
        };
      }
      if (specialtyFee.base_rate_per_100_sf) {
        return {
          description: `${trade} permit (per 100 sq ft)`,
          value: `$${specialtyFee.base_rate_per_100_sf.toLocaleString()}`,
        };
      }
    }
  }

  // Fall back to new buildings rate
  if (feeSchedule.new_buildings_and_additions?.up_to_4_stories) {
    const rate = feeSchedule.new_buildings_and_additions.up_to_4_stories;
    if (rate.base_rate_per_100_sf) {
      return {
        description: "Building permit (per 100 sq ft)",
        value: `$${rate.base_rate_per_100_sf.toLocaleString()}`,
      };
    }
  }

  return null;
}

/**
 * Get the relevant department contact based on trade
 */
function getRelevantContact(
  contacts: Jurisdiction["departmentContacts"],
  trade: string
): { name: string; phone?: string; email?: string } | null {
  if (!contacts) return null;

  const tradeKey = trade.toLowerCase().replace(/[^a-z]/g, "_");

  // Try to find trade-specific contact
  const tradeMapping: Record<string, string> = {
    electrical: "electrical",
    plumbing: "plumbing",
    hvac: "mechanical",
    mechanical: "mechanical",
    roofing: "main",
    general: "main",
  };

  const contactKey = tradeMapping[tradeKey] || "main";
  const contact = contacts[contactKey] || contacts.main;

  if (contact) {
    return {
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
    };
  }

  return null;
}

export function QuickReferenceCard({
  jurisdiction,
  trade,
  projectValue,
}: QuickReferenceCardProps) {
  const fee = getRelevantFee(jurisdiction.feeSchedule, trade);
  const contact = getRelevantContact(jurisdiction.departmentContacts, trade);
  const timeline = jurisdiction.processingTimelines?.first_review;
  const scheduling = jurisdiction.inspectionRequirements?.scheduling;
  const noc = jurisdiction.noticeOfCommencement;

  // Get the applicable NOC threshold based on trade
  // Migration 043 supports per-category thresholds (e.g., { default: 5000, mechanical: 15000 })
  const getNocThreshold = (): { amount: number; description?: string } | null => {
    if (!noc) return null;

    // Check for trade-specific threshold first
    if (noc.thresholds) {
      const tradeKey = trade.toLowerCase();
      const tradeThreshold = noc.thresholds[tradeKey];
      if (tradeThreshold !== undefined) {
        return { amount: tradeThreshold, description: `${trade} projects over $${tradeThreshold.toLocaleString()}` };
      }
      // Fall back to default threshold
      if (noc.thresholds.default !== undefined) {
        return { amount: noc.thresholds.default, description: noc.threshold_description };
      }
    }

    // Fall back to legacy single threshold
    if (noc.threshold) {
      return { amount: noc.threshold, description: noc.threshold_description };
    }

    return null;
  };

  const nocThreshold = getNocThreshold();

  // Check if NOC is required based on project value
  const nocRequired = noc?.required && projectValue && nocThreshold && projectValue > nocThreshold.amount;

  // If we have no data to display, don't render the card
  if (!fee && !contact && !timeline && !scheduling && !nocRequired) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Quick Reference
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* NOC Alert - Most important, show first if applicable */}
        {nocRequired && (
          <Alert variant="destructive" className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">
              Notice of Commencement Required
            </AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              <p className="mb-2">
                {nocThreshold?.description || `Required for projects over $${nocThreshold?.amount?.toLocaleString()}`}
              </p>
              {/* Show recording locations (new in migration 043) or fall back to single office */}
              {noc.recording_locations && noc.recording_locations.length > 0 ? (
                <div className="text-sm space-y-1">
                  <p>Record with:</p>
                  {noc.recording_locations.map((loc, idx) => (
                    <div key={idx} className="ml-2">
                      <strong>{loc.name}</strong>
                      {loc.url && (
                        <a
                          href={loc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-amber-600 hover:underline dark:text-amber-400"
                        >
                          <ExternalLink className="h-3 w-3 inline" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {noc.recording_office && (
                    <p className="text-sm">
                      Record with: <strong>{noc.recording_office}</strong>
                    </p>
                  )}
                  {noc.recording_url && (
                    <a
                      href={noc.recording_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm flex items-center gap-1 mt-1 text-amber-600 hover:underline dark:text-amber-400"
                    >
                      Recording Office Website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Fee Estimate */}
          {fee && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg shrink-0">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{fee.description}</p>
                <p className="font-semibold text-lg">{fee.value}</p>
              </div>
            </div>
          )}

          {/* Processing Timeline */}
          {timeline && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg shrink-0">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">First Review</p>
                <p className="font-semibold">{timeline.typical_business_days}</p>
                {timeline.notes && (
                  <p className="text-sm text-muted-foreground">{timeline.notes}</p>
                )}
              </div>
            </div>
          )}

          {/* Department Contact */}
          {contact && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg shrink-0">
                <Phone className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{contact.name}</p>
                {contact.phone && (
                  <a
                    href={`tel:${contact.phone}`}
                    className="font-semibold text-primary hover:underline"
                  >
                    {contact.phone}
                  </a>
                )}
                {contact.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                  >
                    <Mail className="h-4 w-4" />
                    {contact.email}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Inspection Scheduling */}
          {scheduling && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg shrink-0">
                <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inspection Scheduling</p>
                <p className="font-medium">{scheduling.method}</p>
                {scheduling.advance_notice && (
                  <p className="text-sm text-muted-foreground">
                    Request by {scheduling.advance_notice}
                  </p>
                )}
                {scheduling.phone && (
                  <a
                    href={`tel:${scheduling.phone}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {scheduling.phone}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* NOC Requirements List (if applicable but not yet triggered by value) */}
        {noc?.required && !nocRequired && ((noc.requirements && noc.requirements.length > 0) || (noc.required_information && noc.required_information.length > 0)) && (
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">NOC Requirements (if applicable)</span>
              </div>
              <Badge variant="outline">
                Over ${nocThreshold?.amount?.toLocaleString() || noc.threshold?.toLocaleString()}
              </Badge>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 ml-7 list-disc">
              {noc.requirements?.slice(0, 3).map((req, idx) => (
                <li key={idx}>{req}</li>
              ))}
              {noc.required_information?.slice(0, 3).map((info, idx) => (
                <li key={`info-${idx}`}>{info}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
